import Table from '@cloudscape-design/components/table';
import { formatDate } from '@parallel-fluidics/shared/shipping';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import UserEmail from '../Display/userEmail';
import QuoteStatusTag from '../QuoteStatusTag';

const SORTABLE_FIELDS = Object.freeze(['uploadedAt', 'expiresAt']);

function PartsList({
  parts,
  isLoading,
  Pagination = null,
  header = null,
  variant = 'container',
  sorting,
  onSortingChange,
}) {
  const rowData = useMemo(() => parts.map((part) => ({
    id: part.id,
    idDisplay: `#${part.id.substring(0, 6).toUpperCase()}`,
    filename: <Link to={`/part/${part.id}`}>{part.filename}</Link>,
    status: <QuoteStatusTag partId={part.id} />,
    user: part.uid ? <UserEmail userId={part.uid} backupEmail={part.email} /> : 'Loading...',
    uploadedAt: formatDate(part.uploadedAt),
    expiresAt: formatDate(part.expiresAt),
    viewLiveButton: (
      <Link
        to={`${process.env.REACT_APP_WWW_URL}/quotes/view?quote=${part.id}`}
        target="_blank"
      >
        View live
      </Link>
    ),
  })), [parts]);

  const columnDefinitions = useMemo(() => [
    {
      id: 'idDisplay',
      cell: (item) => item.idDisplay,
      header: 'Quote ID',
    },
    {
      id: 'filename',
      cell: (item) => item.filename,
      header: 'Filename',
      minWidth: '120px',
      maxWidth: '240px',
    },
    {
      id: 'status',
      cell: (item) => item.status,
      header: 'Status',
      minWidth: '120px',
      maxWidth: '160px',
    },
    {
      id: 'uploadedAt',
      cell: (item) => item.uploadedAt,
      header: 'Upload Date',
      minWidth: '120px',
      sortingField: 'uploadedAt',
    },
    {
      id: 'expiresAt',
      cell: (item) => item.expiresAt,
      header: 'Expiration Date',
      minWidth: '120px',
      sortingField: 'expiresAt',
    },
    {
      id: 'user',
      cell: (item) => item.user,
      header: 'Owner',
      minWidth: '120px',
    },
    {
      id: 'viewLiveButton',
      cell: (item) => item.viewLiveButton,
      header: 'View',
      minWidth: '120px',
    },
  ], []);

  const sortingColumn = useMemo(
    () => columnDefinitions.find((c) => c.id === sorting?.field) ?? null,
    [columnDefinitions, sorting?.field],
  );

  const handleSortingChange = ({ detail }) => {
    const next = {
      field: detail.sortingColumn?.id,
      isDescending: detail.isDescending,
    };

    onSortingChange(next);
  };

  return (
    <Table
      header={header}
      variant={variant}
      loading={isLoading}
      items={rowData}
      stickyHeader={variant === 'full-page'}
      pagination={Pagination}
      columnDefinitions={columnDefinitions}
      sortingColumn={sortingColumn}
      sortingDescending={sorting?.isDescending}
      onSortingChange={handleSortingChange}
    />
  );
}

PartsList.propTypes = {
  parts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    uploadedAt: PropTypes.number.isRequired,
    expiresAt: PropTypes.number.isRequired,
    filename: PropTypes.string.isRequired,
    uid: PropTypes.string,
    email: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool.isRequired,
  Pagination: PropTypes.node,
  header: PropTypes.node,
  variant: PropTypes.string,
  sorting: PropTypes.shape({
    field: PropTypes.oneOf(SORTABLE_FIELDS).isRequired,
    isDescending: PropTypes.bool.isRequired,
  }).isRequired,
  onSortingChange: PropTypes.func.isRequired,
};

export default PartsList;
